/**
 * Registro de vistas, widgets y paneles — armazón del visor de mecanismo.
 *
 * Sustituye al condicional cableado que elegía la vista central (`admin` →
 * panel de administración, `ficheros` → explorador). Con dos mecanismos más,
 * ese condicional es exactamente el sitio donde el genérico se contamina: cada
 * instancia nueva obligaría a tocarlo, y «instanciar no toca el genérico» es lo
 * que sostiene el corte.
 *
 * INVERSIÓN DE DEPENDENCIA (§6.2 regla 1 del corte): el genérico **nunca
 * importa** de un mecanismo. Es el paquete de cada mecanismo el que se registra
 * aquí; uno desinstalado desaparece sin dejar referencias colgando. Por eso
 * `widgetDe`/`panelDe` devuelven `undefined` sin ruido: un widget que no está
 * registrado no se pinta, y no rompe la pantalla.
 *
 * Los mecanismos entran como UNA vista registrada por prefijo, resuelta por
 * código: `mecanismo:MEC-XXX`. No hay una entrada por mecanismo.
 */

export const PREFIJO_MECANISMO = 'mecanismo:'

const VISTAS = new Map()
const VISTAS_POR_PREFIJO = []
const WIDGETS = new Map()
const PANELES = new Map()

/** Vista de una sección concreta del menú (`admin`, `ficheros`…). */
export function registrarVista(id, componente) {
  VISTAS.set(id, componente)
}

/**
 * Vista de una FAMILIA de secciones. La usan los mecanismos: una sola vista
 * atiende a todos los `mecanismo:*`, que es lo que hace que el tercero no
 * requiera tocar nada aquí.
 */
export function registrarVistaPorPrefijo(prefijo, componente) {
  VISTAS_POR_PREFIJO.push({ prefijo, componente })
}

/** Componente para un id de ítem de menú, o `undefined` si no hay ninguno. */
export function vistaDe(id) {
  if (!id) return undefined
  if (VISTAS.has(id)) return VISTAS.get(id)
  return VISTAS_POR_PREFIJO.find(v => id.startsWith(v.prefijo))?.componente
}

/** `mecanismo:MEC-XXX` → `MEC-XXX`; cualquier otra cosa → null. */
export function codigoDeVista(id) {
  return id?.startsWith(PREFIJO_MECANISMO) ? id.slice(PREFIJO_MECANISMO.length) : null
}

// --- Puntos de extensión declarados (§6.1) ---------------------------------
// Un widget recibe únicamente: el valor ya proyectado, lo declarado en el
// descriptor y un callback de cambio. NO accede a la red por su cuenta, y NO
// decide nada: la validación semántica vive en el kernel, siempre. Un widget es
// una comodidad, nunca un control.

export function registrarWidget(id, componente) {
  WIDGETS.set(id, componente)
}

export function widgetDe(id) {
  return id ? WIDGETS.get(id) : undefined
}

export function registrarPanel(id, componente) {
  PANELES.set(id, componente)
}

export function panelDe(id) {
  return id ? PANELES.get(id) : undefined
}

/** Solo para tests: el registro es global por módulo. */
export function limpiarRegistro() {
  VISTAS.clear()
  VISTAS_POR_PREFIJO.length = 0
  WIDGETS.clear()
  PANELES.clear()
}
